import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CountdownTimer } from './CountdownTimer';
import { Check, Gift, Lock, CalendarCheck, ShoppingCart, ChefHat, ChevronDown, ClipboardList, Zap, Download, ShieldCheck, UtensilsCrossed, Users, Globe } from 'lucide-react';
interface ValueStackProps {
  basePrice?: number;
  showFlashBonus?: boolean;
}
// USD formatter: 15.9 -> "15.90"
function formatUSD(n: number) {
  return n.toFixed(2);
}
const CHECKOUT_URL = 'https://pay.hotmart.com/T104716980E?checkoutMode=10';
// Today's date resolved in the visitor's own timezone -> "martes 8 de septiembre"
function formatToday() {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    }).format(new Date());
  } catch (e) {
    return '';
  }
}
export function ValueStack({
  basePrice = 15.9,
  showFlashBonus
}: ValueStackProps) {
  const [countdownVisible, setCountdownVisible] = useState(true);
  const [showBonusDetails, setShowBonusDetails] = useState(false);
  // Today's date in the visitor's own timezone/locale (mirrors their location)
  const [todayLabel, setTodayLabel] = useState(() => formatToday());
  const fireInitiateCheckout = () => {
    try {
      if (typeof (window as any).fbq === 'function') {
        ;
        (window as any).fbq('track', 'InitiateCheckout');
      }
    } catch (e) {
      // silently fail
    }
  };
  const handleCtaClick = () => {
    fireInitiateCheckout();
  };
  // Check on mount if the 24h timer already expired
  useEffect(() => {
    const startTime = localStorage.getItem('nutripeques-countdown-start');
    if (startTime) {
      const endTime = parseInt(startTime, 10) + 24 * 60 * 60 * 1000;
      if (Date.now() >= endTime) {
        setCountdownVisible(false);
      }
    }
  }, []);
  // Keep the "solo hoy" date accurate if the tab stays open past midnight
  useEffect(() => {
    const refresh = () => setTodayLabel(formatToday());
    document.addEventListener('visibilitychange', refresh);
    const interval = window.setInterval(refresh, 60 * 1000);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.clearInterval(interval);
    };
  }, []);
  const stackItems = [{
    name: '📘 Guía de Fundamentos',
    value: '$18.40 USD'
  }, {
    name: '🌞 Mañanas con Energía (16 desayunos)',
    value: '$9.80 USD'
  }, {
    name: '🍲 Platos que Nutren (22 comidas)',
    value: '$14.70 USD'
  }, {
    name: '🍎 Snacks Inteligentes (12 meriendas)',
    value: '$9.80 USD'
  }, {
    name: '🎉 Gustitos Conscientes (10 recetas)',
    value: '$7.35 USD'
  }, {
    name: '📋 Sistema NutriPeques planificación semanal',
    value: '$12.25 USD'
  }, {
    name: '📊 Tabla de respuestas para conflictos en mesa',
    value: '$6.15 USD'
  }, {
    name: '💡 Tips de congelación y reutilización',
    value: '$4.90 USD'
  }];
  const bonusDetails = [{
    number: 1,
    icon: ChefHat,
    iconColor: 'text-[#6A1B9A]',
    iconBg: 'bg-[#F3E5F5]',
    accentColor: 'border-[#6A1B9A]',
    badgeBg: 'bg-[#6A1B9A]',
    title: '"Guía Mini-Chefs: Estrategia de Exposición"',
    value: '$22.05 USD',
    image: 'https://cdn.magicpatterns.com/uploads/a6Dgd47E2nydzg7Q35XMHZ/minichefs.png',
    intro: 'Si tu hijo rechaza todo lo nuevo, el problema no empieza en el plato. La Guía Mini-Chefs te enseña a usar micro-tareas de 3 minutos para que tu hijo se exponga a nuevos alimentos sin presión.',
    features: ['El Mapa de Progreso (mirar → tocar → oler → probar)', 'Tareas por edad (1–2 / 3–4 / 5–7 años)', 'Scripts exactos para invitar sin presión y responder al "NO"', 'Plan de exposición de 7 días + tabla de seguimiento', '12 "Misiones Mini-Chefs" listas para usar', 'Checklist imprimible']
  }, {
    number: 2,
    icon: Users,
    iconColor: 'text-[#1565C0]',
    iconBg: 'bg-[#E3F2FD]',
    accentColor: 'border-[#1565C0]',
    badgeBg: 'bg-[#1565C0]',
    title: 'La Mesa en Familia — Comunidad Privada NutriPeques',
    value: '$95.40 USD',
    image: 'https://cdn.magicpatterns.com/uploads/9rsoJU9kGtZj8cFEGBKwya/comunidadnutripeques.png',
    intro: 'El grupo privado de Facebook exclusivo para familias NutriPeques. Un espacio real, sin juicios, donde madres y padres que están exactamente donde estás vos comparten sus avances, dudas y victorias en la mesa. Eso no lo da un PDF — lo da una comunidad.',
    features: ['Acceso inmediato después de tu compra', 'Soporte real cuando una receta no sale como esperabas', 'Inspiración de familias reales (no de influencers)', 'Tips y recetas extra solo dentro del grupo', 'El lugar para celebrar cuando tu hijo prueba algo nuevo', 'Preguntas respondidas por la comunidad y el equipo NutriPeques']
  }];
  const bonusStackItems = [{
    icon: ChefHat,
    iconColor: 'text-[#6A1B9A]',
    iconBg: 'bg-[#F3E5F5]',
    name: 'Guía Mini-Chefs: Estrategia de Exposición',
    value: '$22.05 USD',
    animate: false
  }, {
    icon: Users,
    iconColor: 'text-[#1565C0]',
    iconBg: 'bg-[#E3F2FD]',
    name: 'La Mesa en Familia — Comunidad Privada',
    value: '$95.40 USD',
    animate: true
  }];
  return <section id="oferta" className="py-20 px-4 bg-gradient-to-b from-[#FFF8F0] to-[#FFE0B2]/50" data-id="element-555" data-mp="3atyf3tv79bb1">
      <div className="max-w-lg md:max-w-3xl mx-auto" data-id="element-556" data-mp="3pmrxbn1uw5je">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-white rounded-[2rem] shadow-2xl overflow-hidden border-4 border-[#FF8A65] relative" data-id="element-557" data-mp="3kmne3p9kcwph">
          {/* Header */}
          <div id="oferta-header" className="bg-[#FF8A65] text-white text-center py-6 px-4" data-id="element-558" data-mp="36s4h5tce2lek">
            <h2 className="text-2xl md:text-3xl font-black tracking-wide" data-id="element-559" data-mp="31x79oqurck3s">
              TODO LO QUE NECESITÁS — UN SOLO PRECIO
            </h2>
          </div>

          <div className="p-6 md:p-10" data-id="element-560" data-mp="h3u39k866lz0">
            {/* Stack List */}
            <div className="space-y-3 mb-4" data-id="element-561" data-mp="o8bxmw2xhg9k">
              {stackItems.map((item, idx) => <div key={idx} className="flex justify-between items-center border-b border-gray-100 pb-3" data-id="element-562" data-mp="oafzz9l6i77q" data-mpi={idx}>
                  <div className="flex items-center gap-2" data-id="element-563" data-mp="y69m9kop9f2p">
                    <Check className="w-5 h-5 text-[#4CAF50] shrink-0" data-id="element-564" data-mp="y6sto7gq4l2h" />
                    <span className="text-gray-700 font-medium" data-id="element-565" data-mp="134b5c5m4lee6">
                      {item.name}
                    </span>
                  </div>
                  <span className="text-gray-400 line-through text-sm shrink-0 ml-4" data-id="element-566" data-mp="5fr9tt2361cm">
                    {item.value}
                  </span>
                </div>)}
              <div className="flex justify-between items-center bg-[#FFF3E0] p-3 rounded-xl border border-[#FFE0B2]" data-id="element-567" data-mp="ff02mil7t9xe">
                <div className="flex items-center gap-2" data-id="element-568" data-mp="k7t6l48v0e8f">
                  <Gift className="w-5 h-5 text-[#E65100] shrink-0" data-id="element-569" data-mp="3ixa3bt37a0ke" />
                  <span className="text-[#E65100] font-bold" data-id="element-570" data-mp="3nvlb7y9xq0zz">
                    Todas las actualizaciones futuras
                  </span>
                </div>
                <span className="text-[#E65100] font-black shrink-0 ml-4" data-id="element-571" data-mp="24zny1zlo1059">
                  GRATIS
                </span>
              </div>
            </div>

            {/* Bonus items in stack */}
            <div className="mb-8" data-id="element-572" data-mp="1vxxvpyq8wgsp">
              <div className="flex items-center gap-2 mb-3 mt-6" data-id="element-573" data-mp="1r0y0kiuxoygd">
                <Gift className="w-5 h-5 text-[#E65100]" data-id="element-574" data-mp="1r0fs8nuzyts3" />
                <span className="text-sm font-bold text-[#E65100] uppercase tracking-wider" data-id="element-575" data-mp="1m1ux32ulr0ai">
                  + 2 Regalos especiales gratis hoy
                </span>
              </div>

              <div className="space-y-2" data-id="element-576" data-mp="1d8rksxvp0thg">
                {bonusStackItems.map((bonus, idx) => {
                const BonusIcon = bonus.icon;
                const rowContent = <>
                      <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-577" data-mp="13atwkadu2wou">
                        <div className={`${bonus.iconBg} p-1.5 rounded-lg shrink-0`} data-id="element-578" data-mp="13cxyims69lmh">
                          <BonusIcon className={`w-4 h-4 ${bonus.iconColor}`} data-id="element-579" data-mp="ye3h9p9us54h" />
                        </div>
                        <span className="text-gray-800 font-bold text-[13px] md:text-sm leading-tight" data-id="element-580" data-mp="2n4d9kv1k1iz8">
                          {bonus.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 ml-2" data-id="element-581" data-mp="2dae6nttnpv68">
                        <span className="text-gray-400 line-through text-[11px] md:text-xs" data-id="element-582" data-mp="2i5a5bctefgsb">
                          {bonus.value}
                        </span>
                        <span className="bg-[#4CAF50] text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-583" data-mp="289ywfb4o93sh">
                          GRATIS
                        </span>
                      </div>
                    </>;
                if (bonus.animate) {
                  return <motion.div key={idx} animate={{
                    y: [0, -6, 0, -3, 0]
                  }} transition={{
                    duration: 1.6,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    repeatDelay: 0.4
                  }} className="flex justify-between items-center bg-gradient-to-r from-[#E3F2FD] to-[#FFFDE7] p-3 rounded-xl border-2 border-[#90CAF9] shadow-md" data-id="element-584" data-mp="1kjmi9iiu2atg">
                        {rowContent}
                      </motion.div>;
                }
                return <div key={idx} className="flex justify-between items-center bg-[#FFFDE7] p-3 rounded-xl border border-[#FFF9C4]" data-id="element-585" data-mp="1auz2m3tu00zl" data-mpi={idx}>
                      {rowContent}
                    </div>;
              })}
              </div>

              {/* Leer más toggle */}
              <button onClick={() => setShowBonusDetails(!showBonusDetails)} className="mt-3 flex items-center gap-1.5 mx-auto text-[#E65100] font-bold text-sm hover:text-[#BF360C] transition-colors" data-id="element-586" data-mp="1yluoylfiq3zg">
                {showBonusDetails ? 'Ver menos' : 'Ver detalles de los regalos'}
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${showBonusDetails ? 'rotate-180' : ''}`} data-id="element-587" data-mp="23keeswx8fzx3" />
              </button>

              {/* Expandable bonus details */}
              <AnimatePresence data-id="element-588" data-mp="kuybr4h7wjbf">
                {showBonusDetails && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.4
              }} className="overflow-hidden" data-id="element-589" data-mp="ku6ii0b3exob">
                    <div className="mt-4 space-y-6" data-id="element-590" data-mp="13kxtt7en4y5c">
                      {bonusDetails.map((bonus, idx) => {
                    const BonusDetailIcon = bonus.icon;
                    return <div key={idx} className={`bg-gray-50 rounded-2xl p-5 border-l-4 ${bonus.accentColor}`} data-id="element-591" data-mp="ukro47p6p2gq" data-mpi={idx}>
                            <div className="flex items-center gap-3 mb-3" data-id="element-592" data-mp="3k9tjyou2v143">
                              <div className={`${bonus.iconBg} p-2 rounded-xl`} data-id="element-593" data-mp="3k7qovqe2ihsi">
                                <BonusDetailIcon className={`w-5 h-5 ${bonus.iconColor}`} data-id="element-594" data-mp="3f9p6z8sdooim" />
                              </div>
                              <div data-id="element-595" data-mp="1zs2cvwbjx58i">
                                <span className={`${bonus.badgeBg} text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider`} data-id="element-596" data-mp="1zt3xaregj42w">
                                  Regalo {bonus.number}
                                </span>
                                <h4 className="text-base font-black text-gray-900 mt-1" data-id="element-597" data-mp="28m7acyl6xex0">
                                  {bonus.title}
                                </h4>
                              </div>
                            </div>

                            <div className="flex justify-center mb-4" data-id="element-598" data-mp="2nhdcvbl2uvll">
                              <div className="relative" data-id="element-599" data-mp="2riyoktlvl3u7">
                                <div className="absolute inset-0 bg-gradient-to-b from-orange-200/60 via-orange-100/40 to-transparent rounded-2xl blur-xl scale-110 -z-10" data-id="element-600" data-mp="2rifhk7xrjzuy" />
                                <img src={bonus.image} alt={bonus.title} className="w-full max-w-[220px] h-auto rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12),0_4px_12px_rgba(255,138,101,0.25)] transform hover:scale-[1.02] transition-transform duration-300" data-id="element-601" data-mp="x68sc581bijn" />
                              </div>
                            </div>

                            <p className="text-gray-600 text-sm mb-4 leading-relaxed" data-id="element-602" data-mp="29i4e95iskq6y">
                              {bonus.intro}
                            </p>

                            <div className="space-y-2" data-id="element-603" data-mp="2je7gschk2quf">
                              <p className="text-xs font-bold text-gray-900 flex items-center gap-1.5" data-id="element-604" data-mp="2jcmjif3uwtsv">
                                <ClipboardList className="w-3.5 h-3.5 text-gray-500" data-id="element-605" data-mp="2t4sk2d86cjjo" />
                                Incluye:
                              </p>
                              <ul className="space-y-1.5" data-id="element-606" data-mp="2o67ioah6rttn">
                                {bonus.features.map((feature, fIdx) => <li key={fIdx} className="flex items-start gap-2 text-sm" data-id="element-607" data-mp="32604pshrplva" data-mpi={fIdx}>
                                    <Check className="w-4 h-4 text-[#4CAF50] shrink-0 mt-0.5" data-id="element-608" data-mp="2x7yodfbq80ni" />
                                    <span className="text-gray-700" data-id="element-609" data-mp="3gtgzt55m9wry">
                                      {feature}
                                    </span>
                                  </li>)}
                              </ul>
                            </div>

                            <p className="text-center text-sm font-bold text-gray-900 mt-4" data-id="element-610" data-mp="3bsjisvwcf8s1">
                              Valor:{' '}
                              <span className="line-through text-gray-400" data-id="element-611" data-mp="2y03d1nc24wnf">
                                {bonus.value}
                              </span>{' '}
                              <span className="text-[#4CAF50]" data-id="element-612" data-mp="2o409qe5gyryw">GRATIS hoy</span>
                            </p>
                          </div>;
                  })}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Full bundle mockup */}
            <div id="oferta-resumen" className="text-center mb-8 scroll-mt-4" data-id="element-613" data-mp="2k26dqj67yc52">
              <h3 className="text-lg md:text-xl font-black text-gray-900 leading-snug mb-1" data-id="element-614" data-mp="2c0xovul9gsd3">
                Esto es todo lo que recibes hoy con tu compra de NutriPeques.
              </h3>
              <p className="text-base md:text-lg font-bold text-[#E65100] mb-5" data-id="element-615" data-mp="22be5mntmywhp">
                Todo lo que necesitas para que coma bien
              </p>
              <div className="relative flex justify-center" data-id="element-616" data-mp="1y90wdpfokgg6">
                <div className="absolute inset-0 bg-gradient-to-b from-orange-200/50 via-orange-100/30 to-transparent rounded-3xl blur-2xl scale-105 -z-10" data-id="element-617" data-mp="1tb8xgbgmbu2h" />
                <motion.img src="https://cdn.magicpatterns.com/uploads/sEf5nMTQDwSGPzc8SmxKbh/nutripequesmasminichefs.png" alt="NutriPeques + Guía Mini-Chefs — Todo lo que recibes" className="w-full max-w-md md:max-w-lg h-auto rounded-3xl border-4 border-white shadow-[0_16px_50px_rgba(0,0,0,0.2),0_6px_20px_rgba(255,138,101,0.35),0_0_0_1px_rgba(0,0,0,0.05)]" animate={{
                y: [0, -8, 0]
              }} transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-618" data-mp="1odpjzdrqvlbl" />
              </div>
            </div>

            {/* Pricing Area */}
            <div className="bg-[#FDFBF7] rounded-2xl p-6 md:p-8 text-center border border-gray-200 mb-8 relative overflow-hidden" data-id="element-619" data-mp="156zudwqtmevg">
              <div className="absolute top-0 right-0 bg-[#E65100] text-white text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-620" data-mp="198uwv4khmyat">
                67% DE DESCUENTO
              </div>

              <div className="mb-2" data-id="element-621" data-mp="hh5jvltz52ed">
                <span className="text-gray-500 font-medium" data-id="element-622" data-mp="hi897pbnews6">Valor total: </span>
                <span className="text-gray-400 line-through text-lg" data-id="element-623" data-mp="lir3rr4xegy2">
                  $200.80 USD
                </span>
              </div>

              <div className="mb-4" data-id="element-624" data-mp="qjev6dx2gzst">
                <span className="text-gray-600 font-bold" data-id="element-625" data-mp="1wyfpygacvbxt">
                  Precio regular:{' '}
                </span>
                <span className="text-gray-500 line-through text-xl" data-id="element-626" data-mp="21wh9117s7b9a">
                  $47.90 USD
                </span>
              </div>

              <div className="text-center mb-2" data-id="element-627" data-mp="1t7s4farydqz">
                <span className="block text-sm font-bold text-[#4CAF50] uppercase tracking-wider mb-1 text-balance" data-id="element-628" data-mp="3tzlabyyfjrwf">
                  Oportunidad única en esta página — solo hoy {todayLabel}
                </span>
                <div className="flex justify-center items-start gap-1" data-id="element-629" data-mp="6v5gfny17yqs">
                  <span className="text-3xl font-bold text-[#2E7D32] mt-2" data-id="element-630" data-mp="3f9e1may85n8x">
                    $
                  </span>
                  <span className="text-6xl md:text-7xl font-black text-[#2E7D32] leading-none" data-id="element-631" data-mp="3abvoe8d6clv4">
                    {formatUSD(basePrice)}
                  </span>
                  <span className="text-xl font-bold text-[#2E7D32] mt-2" data-id="element-632" data-mp="3k9a6eceqxipj">
                    USD
                  </span>
                </div>
              </div>
            </div>

            {/* Urgency — 24h countdown only when not expired */}
            {countdownVisible && <div className="text-center mb-8" data-id="element-633" data-mp="2ri8mjy03yyzo">
                <p className="text-[#D84315] font-bold mb-2" data-id="element-634" data-mp="17225chsybxvm">
                  ⏰ Esta oferta desaparece en:
                </p>
                <CountdownTimer hours={24} storageKey="nutripeques-countdown-start" onExpire={() => setCountdownVisible(false)} data-id="element-635" data-mp="1bulijdnq8qzw" />
                
              </div>}

            {/* CTA and Order Bumps */}
            <div className="flex flex-col items-center" data-id="element-636" data-mp="123qz0h1v85i5">
              <a href={CHECKOUT_URL} onClick={handleCtaClick} className="w-full min-h-[72px] bg-[#4CAF50] hover:bg-[#388E3C] text-white text-xl md:text-2xl font-black rounded-2xl shadow-[0_8px_0_#2E7D32] hover:shadow-[0_4px_0_#2E7D32] hover:translate-y-1 transition-[transform,box-shadow,background-color] duration-150 ease-out flex items-center justify-center text-center px-4 py-4 mb-3 cursor-pointer" data-id="element-637" data-mp="1psa6wvh06g9p">
                → QUIERO EMPEZAR A NUTRIR CON CALMA
              </a>

              {/* Currency disclaimer */}
              <p className="flex items-start justify-center gap-2 text-center text-xs md:text-sm text-gray-500 leading-relaxed max-w-md mb-6" data-id="element-638" data-mp="ad2tytx2mfz7">
                <Globe className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" data-id="element-639" data-mp="10zs3hjqlodvn" />
                <span data-id="element-640" data-mp="wyyhwx0mrb5x">
                  En la próxima página de pago vas a poder pagar en tu moneda
                  local y ver la conversión automática.
                </span>
              </p>

              {/* Trust Badges — after first CTA */}
              <div className="w-full grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-id="element-641" data-mp="1kpucg1wh0kgv">
                <div className="flex items-center justify-center gap-2.5 bg-[#FFF8F0] border border-[#FFE0B2] rounded-xl py-3.5 px-3 shadow-[0_4px_12px_rgba(255,138,101,0.15)]" data-id="element-642" data-mp="1fr0zqbp0cxhr">
                  <Zap className="w-5 h-5 text-[#FF8A65] shrink-0" data-id="element-643" data-mp="1ay7vrq1d5t6q" />
                  <span className="text-gray-800 font-bold text-xs md:text-sm" data-id="element-644" data-mp="160epkx028z6g">
                    Acceso inmediato
                  </span>
                </div>
                <div className="flex items-center justify-center bg-white border border-[#BBDEFB] rounded-xl py-1 px-2 shadow-[0_4px_12px_rgba(0,150,255,0.15)] overflow-hidden" data-id="element-645" data-mp="1yq561cp60d5q">
                  <img src="https://cdn.magicpatterns.com/uploads/594Gg7CjuM898zM2Kyfci4/Mercado_Pago.svg.png" alt="Mercado Pago" className="h-14 md:h-16 max-w-full w-auto shrink-0 object-contain" data-id="element-646" data-mp="263e722hrlv78" />
                </div>
                <div className="flex items-center justify-center gap-2.5 bg-[#FFF8F0] border border-[#FFE0B2] rounded-xl py-3.5 px-3 shadow-[0_4px_12px_rgba(255,138,101,0.15)]" data-id="element-647" data-mp="2ky0rmys04nhs">
                  <Download className="w-5 h-5 text-[#FF8A65] shrink-0" data-id="element-648" data-mp="2oy1hdfkjwl45" />
                  <span className="text-gray-800 font-bold text-xs md:text-sm" data-id="element-649" data-mp="2tx5tof2ruyyp">
                    Descarga directa
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2.5 bg-[#E8F5E9] border border-[#C8E6C9] rounded-xl py-3.5 px-3 shadow-[0_4px_12px_rgba(76,175,80,0.15)]" data-id="element-650" data-mp="33p1sjscomt3z">
                  <ShieldCheck className="w-5 h-5 text-[#4CAF50] shrink-0" data-id="element-651" data-mp="3cp6ydy289jg1" />
                  <span className="text-gray-800 font-bold text-xs md:text-sm" data-id="element-652" data-mp="3cn3z0nf6qdyn">
                    Garantía de 30 días
                  </span>
                </div>
              </div>

              {/* ORDER BUMPS */}
              

              {/* Trust Badges */}
              

              {/* Summary Stack */}
              

              {/* Second CTA Button */}
              
            </div>
          </div>
        </motion.div>

        <p className="text-center text-gray-600 italic mt-8 text-lg" data-id="element-653" data-mp="yy0np57asl7f">
          "No necesitas hacerlo perfecto. Necesitas empezar."
        </p>
      </div>
    </section>;
}